import { type ComponentProps, type Ref } from "react";
import { type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { buttonGroupVariants } from "./styles";

export interface ButtonGroupProps extends ComponentProps<"div">, VariantProps<typeof buttonGroupVariants> {
  ref?: Ref<HTMLDivElement>;
  /**
   * How the grouped controls are arranged.
   * - `joined` — children sit flush as one continuous control; the group supplies the outer border
   *   and radius and the children carry none, so mixed control types stay consistent.
   * - `separated` — children keep their own shape and chrome, with a gap between them.
   * - `inline` — chrome-free: the group draws nothing and the children sit flush in their own
   *   shape. For a toolbar of `inline` controls inside a surface that already provides the chrome.
   * @default "joined"
   */
  layout?: "joined" | "separated" | "inline";
  /**
   * Corner shape for the whole group (same values as Button's `radius`), and it wins over each
   * child's own. Under `joined` it shapes the group's outer corners; under `separated`/`inline` it
   * is applied to every child. Leave unset to let children keep their own.
   */
  radius?: "default" | "round" | "pill";
}

/**
 * Arranges a set of sibling controls as a single visual group. Consumed as `<Button.Group>`.
 *
 * Under `joined`, the group draws the border and corner radius itself and flattens its children,
 * so the children need not agree on either — a `Select` (or any other bordered control) joins a
 * row of `Button`s without the two ends disagreeing about radius or border color. Set the shape
 * with `variant`/`color` on each child as usual; those still drive fill and ink, just not chrome.
 *
 * @example
 * ```tsx
 * <Button.Group>
 *   <Button variant="outline">Prev</Button>
 *   <Button variant="outline">Next</Button>
 * </Button.Group>
 *
 * // heterogeneous children are supported under `joined`
 * <Button.Group>
 *   <Select value={view} onValueChange={setView}>
 *     <Select.Trigger><Select.Value /></Select.Trigger>
 *     <Select.Content>{/* … *\/}</Select.Content>
 *   </Select>
 *   <Button variant="outline" iconOnly hoverLabel="Filters"><Filter /></Button>
 * </Button.Group>
 *
 * <Button.Group layout="separated">
 *   <Button>Save</Button>
 *   <Button variant="ghost">Cancel</Button>
 * </Button.Group>
 *
 * // chrome-free toolbar; `radius` gives the select and the icon button the same 12px corners
 * <Button.Group layout="inline" radius="round">
 *   <Select value={view} onValueChange={setView}>
 *     <Select.Trigger variant="inline"><Select.Value /></Select.Trigger>
 *     <Select.Content>{/* … *\/}</Select.Content>
 *   </Select>
 *   <Button variant="inline" color="muted" iconOnly hoverLabel="Filters"><Filter /></Button>
 * </Button.Group>
 * ```
 */
export const ButtonGroup = ({ className, layout, radius, ref, ...props }: ButtonGroupProps) => (
  <div
    ref={ref}
    role="group"
    data-slot="button-group"
    className={cn(buttonGroupVariants({ layout, radius }), className)}
    {...props}
  />
);
